import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/ApiService';
import Footer from '../Footer';
import { useCollectorPreferences } from '../../hooks/useCollectorPreferences';
import { CollectorHome } from '../collector/CollectorHome';
import PageLoader from '../ui/PageLoader';
import HomeFeedPanel from './HomeFeedPanel';
import HomeMyGarage from './HomeMyGarage';
import type { AuthenticatedHomeData, HomeFeedSection, HomeFeedSummary, HomeQuickAction } from './homeTypes';
import ContributorRequestsPanel from '../contributors/ContributorRequestsPanel';
import { useOnboarding } from '../../hooks/useOnboarding';
import {
  OnboardingResumeCard,
  OnboardingWelcomeCard,
  SetupChecklistCard,
} from '../onboarding/OnboardingCards';
import {
  buildBuilderChecklist,
  checklistProgress,
  shouldShowBuilderChecklist,
  shouldShowOnboardingResume,
  shouldShowWelcome,
  type ChecklistStep,
} from '../../utils/onboarding';
import { vehicleProfilePath, resolveVehicleNavSlug } from '../../utils/vehicleUrls';
import { DirectoryBrowseLayout, type SectionRailAnchor, type SectionRailGroup } from '../ui/DirectoryBrowseLayout';
import { ownerProfilePath } from '../../navigation/browseDestinations';
import { HomeQuickActionsRail, HomeSnapshotRail } from './HomeQuickActionsRail';

/**
 * Authenticated home — chronological relevance feed as the primary daily dashboard.
 * Quick actions and snapshot counts live in the left rail.
 */
export const AuthenticatedHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const onboarding = useOnboarding();
  const collectorPreferences = useCollectorPreferences();
  const [garageData, setGarageData] = useState<AuthenticatedHomeData | null>(null);
  const [bootLoading, setBootLoading] = useState(true);
  const [feedSections, setFeedSections] = useState<HomeFeedSection[]>([]);
  const [quickActions, setQuickActions] = useState<HomeQuickAction[]>([]);
  const [feedSummary, setFeedSummary] = useState<HomeFeedSummary | null>(null);

  const loadGarage = useCallback(async () => {
    setBootLoading(true);
    try {
      const home = await apiService.getHomeAuthenticated();
      setGarageData(home);
      void apiService.trackEvent('home_viewed', { metadata: { audience: 'authenticated', surface: 'feed' } });
    } catch {
      setGarageData({});
    } finally {
      setBootLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadGarage();
  }, [loadGarage]);

  const garageVehicles = useMemo(() => garageData?.garage?.vehicles || [], [garageData?.garage?.vehicles]);

  const checklistSteps = useMemo(() => {
    if (!user?.username) return [] as ChecklistStep[];
    const snapshots = garageVehicles.map((vehicle) => {
      const completeness = (vehicle as { completeness?: { percent?: number } }).completeness;
      const slug = resolveVehicleNavSlug(vehicle);
      return {
        id: vehicle.id,
        slugPath: slug ? vehicleProfilePath(user.username, slug) : ownerProfilePath(user.username),
        engine: vehicle.engine,
        transmission: null,
        drivetrain: vehicle.drivetrain,
        chassis: vehicle.chassis,
        description: vehicle.description,
        imageCount: vehicle.image_url ? 1 : 0,
        modificationCount: vehicle.modification_count ?? 0,
        completenessPercent: completeness?.percent ?? 0,
        isPublic: vehicle.is_public,
      };
    });
    return buildBuilderChecklist(snapshots, onboarding.state, user.username);
  }, [garageVehicles, onboarding.state, user?.username]);

  const builderIntent = !collectorPreferences || garageVehicles.length > 0 || collectorPreferences.interests.includes('build') || ['build','both'].includes(collectorPreferences.home_focus);
  const collectFirst = collectorPreferences && (['collect', 'both'].includes(collectorPreferences.home_focus) || (collectorPreferences.home_focus === 'auto' && (collectorPreferences.interests.includes('collect') || (garageVehicles.length === 0 && !collectorPreferences.interests.includes('build')))));
  const showWelcome = builderIntent && shouldShowWelcome(onboarding.state, garageVehicles.length);
  const showChecklist = builderIntent && shouldShowBuilderChecklist(onboarding.state, checklistSteps);
  const showResume = builderIntent && shouldShowOnboardingResume(onboarding.state, checklistSteps);

  const { loading: onboardingLoading, state: onboardingState, markStarted, markCompleted } =
    onboarding;

  useEffect(() => {
    if (!onboardingLoading && (showWelcome || showChecklist) && !onboardingState.started_at) {
      void markStarted();
    }
  }, [
    onboardingLoading,
    onboardingState.started_at,
    markStarted,
    showWelcome,
    showChecklist,
  ]);

  useEffect(() => {
    const { done, total } = checklistProgress(checklistSteps);
    if (total > 0 && done === total && !onboardingState.completed_at) {
      void markCompleted();
    }
  }, [checklistSteps, onboardingState.completed_at, markCompleted]);

  const handleChecklistAction = (step: ChecklistStep) => {
    if (step.actionId === 'add_vehicle') {
      navigate(ownerProfilePath(user?.username, { addVehicle: true }));
      return;
    }
    if (step.href) {
      navigate(step.href);
    }
  };

  const showGarageSection = builderIntent && collectorPreferences?.home_focus !== 'collect' && !(showWelcome && garageVehicles.length === 0);

  const sectionAnchors = useMemo(() => {
    const anchors: SectionRailAnchor[] = [];
    if (showGarageSection) {
      anchors.push({ id: 'home-garage', label: 'My Garage', description: 'Your builds' });
    }
    anchors.push({ id: 'home-feed', label: 'Feed', description: 'Your activity' });
    for (const section of feedSections) {
      anchors.push({
        id: `feed-section-${section.id}`,
        label: section.title,
        description: `${section.items.length} update${section.items.length === 1 ? '' : 's'}`,
      });
    }
    return anchors;
  }, [feedSections, showGarageSection]);

  const railGroups = useMemo(() => {
    const groups: SectionRailGroup[] = [];
    if (quickActions.length > 0) {
      groups.push({
        id: 'quick-actions',
        label: 'Quick actions',
        children: <HomeQuickActionsRail actions={quickActions} />,
      });
    }
    if (feedSummary) {
      groups.push({
        id: 'snapshot',
        label: 'Snapshot',
        children: <HomeSnapshotRail summary={feedSummary} />,
      });
    }
    return groups;
  }, [quickActions, feedSummary]);

  const handleFeedMeta = useCallback(
    ({
      sections,
      quick,
      summary,
    }: {
      sections: HomeFeedSection[];
      quick: HomeQuickAction[];
      summary: HomeFeedSummary | null;
    }) => {
      setFeedSections(sections);
      setQuickActions(quick);
      setFeedSummary(summary);
    },
    []
  );

  if (bootLoading) {
    return <PageLoader message="Loading your home…" />;
  }

  return (
    <div className="page relative bg-[#0a0a0a]">
      <div className="relative z-[1] w-full px-4 sm:px-6 lg:px-8 pt-8 sm:pt-10 pb-4 flex-1">
        <DirectoryBrowseLayout
          title="Home"
          description={feedSummary?.headline || 'Your feed at a glance'}
          mobileTriggerLabel="Home"
          sections={sectionAnchors}
          groups={railGroups}
        >
          {collectFirst ? <CollectorHome /> : null}
          {showWelcome ? (
            <OnboardingWelcomeCard
              onAddVehicle={() => navigate(ownerProfilePath(user?.username, { addVehicle: true }))}
              onExplore={() => {
                void onboarding.dismissWelcome();
                navigate('/discover');
              }}
              onSkip={() => void onboarding.dismissWelcome()}
            />
          ) : null}

          {showChecklist && !showWelcome ? (
            <SetupChecklistCard
              steps={checklistSteps}
              onDismiss={() => void onboarding.dismissChecklist()}
              onStepAction={handleChecklistAction}
            />
          ) : null}

          {showResume && !showWelcome && !showChecklist ? (
            <OnboardingResumeCard onContinue={() => void onboarding.showSetupGuide()} />
          ) : null}

          <ContributorRequestsPanel hideWhenEmpty />

          {showGarageSection ? <HomeMyGarage vehicles={garageVehicles} /> : null}

          <HomeFeedPanel onFeedMeta={handleFeedMeta} />

          <Footer />
        </DirectoryBrowseLayout>
      </div>
    </div>
  );
};

export default AuthenticatedHomePage;
